import React, { useState } from 'react';
import { Building2 } from 'lucide-react';
import { Company } from './companyType';
import { validateEmail, validateZipCode, validateAndFormatPhoneNumber } from '../lib/validator';
import { Button } from '../components/ui/button';
import { Alert } from '../components/ui/alert';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select } from '../components/ui/select';

interface CreateCompanyFormProps {
    initialCompany: Partial<Company>;
    lineOfBusinessOptions: string[];
    stateOptions: string[];
    onSubmit: (company: Partial<Company>) => void;
}

const CreateCompanyForm: React.FC<CreateCompanyFormProps> = ({
    initialCompany,
    lineOfBusinessOptions,
    stateOptions,
    onSubmit
}) => {
    const [formData, setFormData] = useState<Partial<Company>>(initialCompany);
    const [errorMessage, setErrorMessage] = useState<string>('');
    const [isSubmitting, setIsSubmitting] = useState(false);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));

        if (name === 'email') {
            setErrorMessage('');
        }
    };

    const validateAllFields = () => {
        const errors: string[] = [];

        if (formData.zip !== undefined) {
            const zipResult = validateZipCode(formData.zip, true);
            if (!zipResult.isValid) {
                errors.push(zipResult.error || 'Invalid ZIP code');
            }
        }

        if (formData.phone !== undefined) {
            const phoneResult = validateAndFormatPhoneNumber(formData.phone);
            if (!phoneResult.isValid) {
                errors.push(phoneResult.error || 'Invalid phone number');
            }
        }

        if (formData.email !== undefined) {
            const emailResult = validateEmail(formData.email);
            if (!emailResult.isValid) {
                errors.push(emailResult.error || 'Invalid email');
            }
        }
        setErrorMessage('');

        if (formData.zip === undefined) errors.push('ZIP code is required');
        if (formData.phone === undefined) errors.push('Phone number is required');
        if (formData.email === undefined) errors.push('Email is required');

        if (errors.length > 0) {
            setErrorMessage(errors.join(', '));
            return false;
        }

        return true;
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (validateAllFields()) {
            setIsSubmitting(true);
            await onSubmit(formData);
            setIsSubmitting(false);
        }
    };

    const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        let errors: string[] = [];
        setErrorMessage('');

        switch (name) {
            case 'zip':
                const zipResult = validateZipCode(value, true);
                if (!zipResult.isValid) {
                    errors.push(zipResult.error || 'Invalid ZIP code');
                } else {
                    setFormData(prev => ({ ...prev, zip: zipResult.value }));
                }
                break;

            case 'phone':
                const phoneResult = validateAndFormatPhoneNumber(value);
                if (!phoneResult.isValid) {
                    errors.push(phoneResult.error || 'Invalid phone number');
                } else {
                    setFormData(prev => ({ ...prev, phone: phoneResult.value }));
                }
                break;

            case 'email':
                const emailResult = validateEmail(value);
                if (!emailResult.isValid) {
                    errors.push(emailResult.error || 'Invalid email');
                }
                break;
        }

        if (errors.length > 0) {
            setErrorMessage(errors.join(', '));
        } else {
            setErrorMessage('');
        }
    };

    return (
        <form onSubmit={handleSubmit} className="space-y-6">
            <h1 className="page-heading">Step 1: Create Company Profile</h1>

            {/* Error Message */}
            {errorMessage && (
                <Alert variant="error">
                    {errorMessage}
                </Alert>
            )}

            {/* Company Details */}
            <div className="space-y-4">
                <h2 className="form-section-heading">Company Details</h2>

                <div>
                    <Label htmlFor="name">
                        Company Name <span className="text-red-500">*</span>
                    </Label>
                    <Input
                        type="text"
                        id="name"
                        name="name"
                        value={formData.name || ''}
                        onChange={handleChange}
                        required
                    />
                </div>

                <div>
                    <Label htmlFor="line_of_business">
                        Line of Business <span className="text-red-500">*</span>
                    </Label>
                    <Select
                        id="line_of_business"
                        name="line_of_business"
                        value={formData.line_of_business || ''}
                        onChange={handleChange}
                        required
                        className="form-input"
                    >
                        <option value="">Select a Line of Business</option>
                        {lineOfBusinessOptions.map((option, index) => (
                            <option key={index} value={option}>{option}</option>
                        ))}
                    </Select>
                </div>
            </div>

            {/* Address */}
            <div className="space-y-4">
                <h2 className="form-section-heading">Address</h2>

                <div>
                    <Label htmlFor="address_1">
                        Address 1 <span className="text-red-500">*</span>
                    </Label>
                    <Input
                        type="text"
                        id="address_1"
                        name="address_1"
                        value={formData.address_1 || ''}
                        onChange={handleChange}
                        required
                    />
                </div>

                <div>
                    <Label htmlFor="address_2">Address 2</Label>
                    <Input
                        type="text"
                        id="address_2"
                        name="address_2"
                        value={formData.address_2 || ''}
                        onChange={handleChange}
                    />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                        <Label htmlFor="city">
                            City <span className="text-red-500">*</span>
                        </Label>
                        <Input
                            type="text"
                            id="city"
                            name="city"
                            value={formData.city || ''}
                            onChange={handleChange}
                            required
                        />
                    </div>

                    <div>
                        <Label htmlFor="state">
                            State <span className="text-red-500">*</span>
                        </Label>
                        <Select
                            id="state"
                            name="state"
                            value={formData.state || ''}
                            onChange={handleChange}
                            required
                            className="form-input"
                        >
                            <option value="">Select a State</option>
                            {stateOptions.map((state: string, index: number) => (
                                <option key={index} value={state}>{state}</option>
                            ))}
                        </Select>
                    </div>

                    <div>
                        <Label htmlFor="zip">
                            Zip <span className="text-red-500">*</span>
                        </Label>
                        <Input
                            type="text"
                            id="zip"
                            name="zip"
                            value={formData.zip || ''}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            required
                        />
                    </div>
                </div>
            </div>

            {/* Contact Information */}
            <div className="space-y-4">
                <h2 className="form-section-heading">Contact Information</h2>

                <div>
                    <Label htmlFor="phone">
                        Phone <span className="text-red-500">*</span>
                    </Label>
                    <Input
                        type="tel"
                        id="phone"
                        name="phone"
                        value={formData.phone || ''}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        required
                    />
                </div>

                <div>
                    <Label htmlFor="email">
                        Email <span className="text-red-500">*</span>
                    </Label>
                    <Input
                        type="email"
                        id="email"
                        name="email"
                        value={formData.email || ''}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        required
                    />
                </div>

                <div>
                    <Label htmlFor="web_url">Web URL</Label>
                    <Input
                        type="text"
                        id="web_url"
                        name="web_url"
                        value={formData.web_url || ''}
                        onChange={handleChange}
                    />
                </div>
            </div>

            <Button type="submit" size="medium" variant="default" disabled={isSubmitting} >
                <Building2 className="size-5" />
                {isSubmitting ? 'Creating Company...' : 'Create Company'}
            </Button>
        </form>
    );
};

export default CreateCompanyForm;